import React, { useEffect, useMemo, useState } from "react";
import Canvas from "../components/editor/Canvas";
import Tools from "../components/editor/Tools";
import TopBar from "../components/editor/TopBar";
import { useDispatch, useSelector } from "react-redux";
import Popover from "../components/common/popovers/Popover";
import {
  MemoizedCanvasProvider,
  useCanvasContext,
} from "../components/editor/CanvasContext";
import { useNavigate, useParams } from "react-router-dom";
import Previewer from "../components/editor/Previewer";
import {
  loadFonts,
  loadTemplateDetails,
  loadUserTemplateDetails,
  saveProject,
} from "../thunks/dashboardThunks";
import GenerationCanvas from "../components/editor/GenerationCanvas";
import MusicPlayer from "../components/common/popovers/MusicPlayer";
import Loader from "../components/common/Loader";
import {
  setIsOnlyAIVoice,
  setIsOnlyAvatarUrl,
  setProjectMusic,
  setProjectType,
  setScriptVolume as setReduxScriptVolume,
} from "../store/project/projectSlice";
import {
  setSlideLoading,
  updateSlideVoice,
  updateSlideVoiceFile,
} from "../store/slides/slidesSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faTrash } from "@fortawesome/free-solid-svg-icons";
import { addAvatar } from "../canvasUtils";
import { loadFont } from "../utils";
import { Modal } from "../components/common/modal";
import { getElevenLabsVoices } from "../thunks/editor/scriptThunks";
import DocumentUpload from "../components/dashboard/UploadDoc";
import ScriptPopover from "../components/common/popovers/ScriptPopover";
import { loadProAvatars } from "../thunks/editor/avatarThunks";
import { setVidboardAvatars } from "../store/avatars/avatarsSlice";
import { defaultFemaleVoice, defaultMaleVoice } from "../constants/states";
import EnterLink from "../components/dashboard/EnterLink";
import { debounce } from "../utils/debounce";
import UseaiPopover from "../components/common/popovers/UseaiPopover";

const Editor = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, loggedIn } = useSelector((state) => state.user);
  const { bottomBarExpanded, popover, previewMode } = useSelector(
    (state) => state.ui
  );
  const appState = useSelector((state) => state.appState);
  const [openSavePopup, setOpenSavePopup] = useState(""); // will store event type "back" || "refresh" ||"dashboard"
  const [changesSaved, setChangesSaved] = useState(false);
  const [documentUploadModal, setDocumentUploadModal] = useState(false);

  const {
    bgMusic,
    type: projectType,
    id: templateId,
    loading: projectLoading,
    isFaceless,
    isDoc,
    isLinkVideo,
    avatar,
    scriptVolume,
    isNonVoice,
    isOnlyAIVoice,
    isAIdubbingVoice,
  } = useSelector((state) => state.project);

  const fontList = useSelector((state) => state.fonts);

  const { loading, currentSlideIndex, slides } = useSelector(
    (state) => state.slides
  );

  const isOnlyAvatar = window.location.pathname.includes("only-avatar");
  const isOnlyAIVoices = window.location.pathname.includes("ai-voices");

  const { proAvatars } = useSelector((state) => state.avatars);

  useEffect(() => {
    dispatch(loadProAvatars());
  }, []);

  useEffect(() => {
    if (changesSaved) {
      handleClose();
    }
  }, [changesSaved, openSavePopup]);

  useEffect(() => {
    const fireEvent = (event) => {
      if (!changesSaved) {
        event.preventDefault();
        event.returnValue = "";
        setOpenSavePopup(event.state ? "back" : "refresh");
      }

      // Prevent the default alert
      return " your work will be lost";
    };

    if (!(window.history.state?.name === "browserBack")) {
      window.history.pushState(
        { name: "browserBack" },
        "on browser back click",
        window.location.href
      );
    } else {
      window.history.replaceState(
        { name: "browserBack" },
        "on browser back click",
        window.location.href
      );
    }
    window.addEventListener("beforeunload", fireEvent);
    window.addEventListener("popstate", fireEvent);

    return () => {
      window.removeEventListener("beforeunload", fireEvent);
      window.removeEventListener("popstate", fireEvent);
    };
  }, [changesSaved, openSavePopup]);

  useEffect(() => {
    if (loggedIn && !isAdmin) {
      const handleContextmenu = (e) => {
        e.preventDefault();
      };
      document.addEventListener("contextmenu", handleContextmenu);
      document.onkeydown = (e) => {
        if (e.key == "F12") {
          e.preventDefault();
        }
        if (e.ctrlKey && e.shiftKey && e.key == "I") {
          e.preventDefault();
        }
        if (e.ctrlKey && e.shiftKey && e.key == "C") {
          e.preventDefault();
        }
        if (e.ctrlKey && e.shiftKey && e.key == "J") {
          e.preventDefault();
        }
        if (e.ctrlKey && e.key == "U") {
          e.preventDefault();
        }
      };
      return function cleanup() {
        document.removeEventListener("contextmenu", handleContextmenu);
      };
    }
  }, [isAdmin, loggedIn]);

  const currentSlide = useMemo(
    () => slides[currentSlideIndex],
    [currentSlideIndex, slides]
  );

  const firstSlideAvatar = useMemo(() => {
    if (isOnlyAvatar) {
      return avatar;
    }
    const filteredSlides = (slides || [])
      .flatMap((slide) =>
        (slide?.objects || []).find(
          (obj) => obj.type === "avatar" && !obj.presenterId
        )
      )
      .filter((s) => s);

    return filteredSlides[0];
  }, [slides]);

  const shouldOpenDocUploadModal = useMemo(() => {
    return (
      (isDoc || isLinkVideo) &&
      !(
        (slides || []).length > 1 ||
        (slides || []).find((slide) => slide.originalScript)
      )
    );
  }, [slides, isDoc, isLinkVideo]);

  useEffect(() => {
    setDocumentUploadModal(!!shouldOpenDocUploadModal);
  }, [shouldOpenDocUploadModal]);

  const { voice, is_user_audio } = useMemo(() => {
    return {
      voice: currentSlide?.voiceFile?.mp3,
      is_user_audio: !!currentSlide?.is_user_audio,
    };
  }, [currentSlide?.voiceFile?.mp3]);

  const generateMode = useSelector((state) => state.ui.generateMode);
  const dispatch = useDispatch();
  const { canvas } = useCanvasContext();

  useEffect(() => {
    if (type) {
      dispatch(setProjectType(type));
    }
  }, [type]);

  useEffect(() => {
    if (
      !isFaceless &&
      currentSlide &&
      canvas &&
      proAvatars.length &&
      templateId &&
      !projectLoading &&
      !shouldOpenDocUploadModal &&
      appState === "idle" &&
      !isNonVoice
    ) {
      const isAvatar = (currentSlide.objects || []).find(
        (obj) => obj.type === "avatar"
      );
      if (!isAvatar) {
        const findAvatar = proAvatars.find(
          (ava) => ava.gender.toLowerCase() === "male"
        );
        const defaultAvatar = firstSlideAvatar || {
          ...findAvatar,
          url: findAvatar?.image,
        };
        dispatch(
          updateSlideVoice(
            (defaultAvatar?.gender || "").toLowerCase() === "female"
              ? defaultFemaleVoice
              : defaultMaleVoice
          )
        );
        if (isOnlyAvatar) {
          dispatch(setIsOnlyAvatarUrl({ ...defaultAvatar }));
        }

        addAvatar(
          canvas,
          {
            ...defaultAvatar,
            top: 100,
            left: 100,
            type: "avatar",
            pro_avatar: true, // send this when pro avatar selected
          },
          dispatch
        );
      }
    }
  }, [
    currentSlide,
    proAvatars,
    canvas,
    templateId,
    firstSlideAvatar,
    projectLoading,
    isFaceless,
    shouldOpenDocUploadModal,
    appState,
    isNonVoice,
  ]);

  const callback = (loading) => dispatch(setSlideLoading(!!loading));

  const resolveFonts = async () => {
    return Promise.all(
      fontList.map((font) => loadFont(font.name, font.fontFile))
    );
  };

  useEffect(() => {
    if (fontList.length) {
      resolveFonts();
    }
  }, [fontList]);

  useEffect(() => {
    if (canvas === null) return;
    dispatch(loadFonts());
    dispatch(getElevenLabsVoices());
    // just for initial call.

    if (id) {
      callback(true);
      if (projectType === "template") {
        dispatch(loadTemplateDetails(id, canvas, navigate, callback));
      } else {
        dispatch(loadUserTemplateDetails(id, canvas, navigate, callback));
      }
    }

    // Cleanup when the component is unmounted
    return () => {
      // Clear any state or resources that need to be cleaned up here
    };
  }, [dispatch, canvas, projectType, id]);

  const removeAudioScript = () => {
    dispatch(updateSlideVoiceFile(null));
  };

  const handleClose = (isCancel) => {
    if (isCancel) {
      dispatch(setIsOnlyAIVoice(false));
      return navigate("/tool/dashboard");
    }
    if (openSavePopup) {
      if (openSavePopup === "refresh") {
        setChangesSaved(false);
      } else {
        navigate("/tool/dashboard");
        dispatch(setIsOnlyAIVoice(false));
      }
    }
    setOpenSavePopup("");
  };

  const onDashboardRedirect = () => {
    setOpenSavePopup("dashboard");
  };
  const handleSave = () => {
    dispatch(saveProject(id, true, () => setChangesSaved(true)));
  };

  const setBackgroundMusicVolume = debounce((vol) => {
    dispatch(
      setProjectMusic({
        ...bgMusic,
        volume: vol,
      })
    );
  }, 400);

  const setScriptVolume = debounce((vol) => {
    dispatch(setReduxScriptVolume(vol));
  }, 400);

  return (
    <>
      {(loading || appState === "saving") && <Loader />}
      <Modal
        hideClose
        onClose={() => handleClose(true)}
        open={openSavePopup}
        center
        confirmText="Save"
        onSubmit={handleSave}
        cancelText="Exit"
        classes={{
          wrapper: "!z-50",
          body: "!z-50",
          backdrop: "!z-50",
        }}
      >
        <FontAwesomeIcon
          icon={faClose}
          onClick={() => setOpenSavePopup()}
          className="absolute top-5 right-5 cursor-pointer"
        ></FontAwesomeIcon>
        Please save your changes before leaving this page!
      </Modal>
      <div className="min-h-screen flex flex-col bg-gray-100 relative h-full">
        <TopBar onDashboardRedirect={onDashboardRedirect} />
        {shouldOpenDocUploadModal && (
          <div className="absolute inset-0 z-30 bg-vb-label-gray2 h-full w-full">
            {isDoc && (
              <DocumentUpload
                className={
                  "absolute inset-0 max-h-[550px] max-w-[1092px] m-auto"
                }
              />
            )}
            {isLinkVideo && (
              <div className="absolute inset-0 z-30 bg-vb-label-gray2 h-full w-full">
                <EnterLink
                  className={
                    "absolute border-2 inset-0 max-h-[550px] max-w-[1092px] m-auto"
                  }
                />
              </div>
            )}
          </div>
        )}

        <div className="flex-grow flex justify-center h-full">
          <div
            className={`${
              isDoc || isLinkVideo ? "flex-grow-[6.5]" : ""
            } w-full"`}
          >
            <Canvas />
          </div>

          {(isDoc || isLinkVideo) && (
            <div className="flex-grow-[3.5] w-full">
              <ScriptPopover />
            </div>
          )}
        </div>
        {isAIdubbingVoice && (
          <div className="flex-grow flex justify-center items-center p-4 border-b border-gray-300 bg-black-200 h-full pb-16">
            {" "}
            <UseaiPopover />{" "}
          </div>
        )}
        {(bgMusic || (is_user_audio && voice)) && (
          <div className="relative">
            <div
              className={`absolute overflow-hidden max-w-[170px] ${
                bottomBarExpanded ? "bottom-[270px]" : "bottom-[80px]"
              } left-10`}
            >
              {bgMusic && (
                <div className="flex items-center space-x-2">
                  <div>
                    <p>
                      Background Music{" "}
                      <FontAwesomeIcon
                        className="cursor-pointer"
                        onClick={() => dispatch(setProjectMusic(null))}
                        icon={faTrash}
                      />
                    </p>
                    <MusicPlayer
                      key={bgMusic?.id}
                      {...bgMusic}
                      className="!justify-start"
                      isActive={true}
                      showVolume={true}
                      setReduxVolume={setBackgroundMusicVolume}
                    />
                  </div>
                </div>
              )}
              {is_user_audio && voice && (
                <div>
                  <p>
                    Your audio script{" "}
                    <FontAwesomeIcon
                      className="cursor-pointer"
                      onClick={() => removeAudioScript(null)}
                      icon={faTrash}
                    />
                  </p>
                  <MusicPlayer
                    key={voice}
                    file={voice}
                    className="!justify-start"
                    isActive={true}
                    currentVolume={scriptVolume}
                    showVolume={true}
                    setReduxVolume={setScriptVolume}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        <Tools />
        {popover && (
          <Popover
            type={popover}
            bottom={bottomBarExpanded ? "270px" : "80px"}
          />
        )}
        {previewMode && <Previewer />}
        {generateMode === true && <GenerationCanvas />}
      </div>
    </>
  );
};

function EditorPage() {
  return (
    <MemoizedCanvasProvider>
      <Editor />
    </MemoizedCanvasProvider>
  );
}

export default EditorPage;
